import React, { useCallback } from "react";
import { Button, Badge } from "@/components/ui";
import { PostServices, PostDetails, PostSocialNetworkDetails, PostStatsEntry } from "@/services/bedrock";
import {
  AlertCircleIcon,
  RefreshCwIcon,
  CheckCircle2Icon,
  ClockIcon,
  Eye,
  ThumbsUp,
  MessageSquare,
  Share2,
  MousePointer2,
  Heart,
  Loader2,
  Bell,
  WifiOff,
  Trash2,
  Send,
  RefreshCw,
  ExternalLinkIcon,
} from "lucide-react";
import moment from "moment";
import { StatItem } from "./StatItem";
import { printDateTime, useSocialCoachSession } from "@/utils";
import { socialNames } from "@/interfaces";
import { toast } from "sonner";

interface PostStatusPanelProps {
  post: PostDetails;
  socialPost?: PostSocialNetworkDetails;
  withWritePermission: boolean;
  isPolling: boolean;
  onStartPolling: (socialPostId: number, postedTimestamp: string | null) => void;
  networkStats?: PostStatsEntry | null;
  handleReload: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; icon: any; theme: string }> = {
  POSTED: {
    label: "Posted",
    icon: CheckCircle2Icon,
    theme: "text-emerald-700 bg-emerald-50 border-emerald-100",
  },
  FAILED: {
    label: "Failed",
    icon: AlertCircleIcon,
    theme: "text-destructive bg-destructive/5 border-destructive/10",
  },
  FAILURE_ACKED: {
    label: "Failed",
    icon: AlertCircleIcon,
    theme: "text-destructive bg-destructive/5 border-destructive/10",
  },
  IN_TRANSIT: {
    label: "Posting In Progress",
    icon: Send,
    theme: "text-amber-700 bg-amber-50 border-amber-100 animate-pulse",
  },
  SKIPPED_NO_CONNECTION: {
    label: "Skipped (Not Connected)",
    icon: WifiOff,
    theme: "text-slate-600 bg-slate-100 border-slate-200",
  },
  DELETED: {
    label: "Deleted",
    icon: Trash2,
    theme: "text-slate-600 bg-slate-100 border-slate-200",
  },
  PENDING: {
    label: "Scheduled",
    icon: ClockIcon,
    theme: "text-blue-700 bg-blue-50 border-blue-100",
  },
};

export function PostStatusPanel({
  post,
  socialPost,
  withWritePermission,
  isPolling,
  onStartPolling,
  networkStats,
  handleReload,
}: PostStatusPanelProps) {
  const { isPlayer } = useSocialCoachSession();
  const [isRetrying, setIsRetrying] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  React.useEffect(() => {
    setShowDeleteConfirm(false);
  }, [socialPost?.id]);

  const handleRetry = async () => {
    if (!socialPost) return;

    setIsRetrying(true);
    try {
      await PostServices.retry(post.id, socialPost.id);
      onStartPolling(socialPost.id, socialPost.postedTimestamp || null);
    } catch (error) {
      console.error("Failed to retry post:", error);
    } finally {
      setIsRetrying(false);
    }
  };

  const status = socialPost?.status || "PENDING";
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  const Icon = config.icon;

  const isFailed = status === "FAILED" || status === "FAILURE_ACKED";
  const isPosted = status === "POSTED";
  const isDeleted = status === "DELETED";
  const isPushNotification = post.scheduledStatus === "IGNORE" && post.deliveryStatus !== "IGNORE";

  const timestamp = isPosted || isFailed ? socialPost?.postedTimestamp : !isDeleted ? post.scheduledDatetimeUtc : null;

  const canDeletePost =
    socialPost &&
    ["FB", "LI", "TW"].includes(socialPost.socialNetworkId) &&
    socialPost.status === "POSTED" &&
    socialPost.externalPostUrl;

  const [isDeleting, setIsDeleting] = React.useState(false);
  const handleDelete = useCallback(
    async (socialPostId: number) => {
      setIsDeleting(true);
      PostServices.updateSocialPost(post.id, socialPostId, { updateType: "DELETE" })
        .then(() => {
          toast.info("Post has been deleted successfully.");
          handleReload();
        })
        .catch(err => {
          console.error("Failed to delete post:", err);
          toast.error("Failed to delete post. Please try again.", {
            description: err.message || err.toString() || "Unknown error",
          });
        })
        .finally(() => {
          setIsDeleting(false);
          setShowDeleteConfirm(false);
        });
    },
    [setIsDeleting, post.id, handleReload, setShowDeleteConfirm]
  );

  return (
    <div className="flex flex-col bg-slate-50/50 h-[400px] overflow-y-auto">
      <div className="p-4 flex flex-col gap-5">
        {/* Status Section */}
        <div className="flex flex-col gap-2.5">
          {isPushNotification && (
            <Badge
              variant="outline"
              className="w-fit gap-1.5 py-1 px-2.5 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border-amber-200/50 shadow-sm"
            >
              <Bell className="size-3" />
              Scheduled Push
            </Badge>
          )}
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Delivery Status</h4>
          <div className="flex flex-col gap-2">
            {isPolling && (
              <div className="flex items-center gap-2 text-blue-700 text-sm font-medium bg-blue-50 p-2.5 rounded-xl border border-blue-100 leading-snug animate-pulse">
                <Loader2 className="size-3.5 shrink-0 animate-spin" />
                Checking for updates...
              </div>
            )}

            <div className={`flex flex-row justify-between items-center gap-1 p-2.5 rounded-xl border ${config.theme}`}>
              <div className={"flex flex-col gap-1"}>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-tight">
                  <Icon className="size-3.5" />
                  {config.label}
                </div>
                {timestamp && (
                  <div className="text-xs opacity-80">{moment(timestamp).format("MMM D, YYYY [at] h:mm A")}</div>
                )}
                {isPosted && socialPost?.externalPostUrl && (
                  <a
                    href={socialPost.externalPostUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs font-semibold underline hover:opacity-80 transition-opacity mt-1.5 gap-1 w-fit"
                  >
                    <ExternalLinkIcon className="size-3 shrink-0" />
                    View on {socialNames[socialPost.socialNetworkId]}
                  </a>
                )}
              </div>
              {status === "PENDING" && (
                <Button variant="ghost" size="icon" className={"cursor-pointer text-blue-200"} onClick={handleReload}>
                  <RefreshCw />
                </Button>
              )}
            </div>

            {isFailed && !isPolling && (
              <div className="flex flex-col gap-2">
                {socialPost?.failedReason && (
                  <div className="text-xs text-destructive/80 italic bg-destructive/2 p-2.5 rounded-lg border border-destructive/5">
                    {socialPost.failedReason}
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1.5 bg-white shadow-sm h-8 text-xs"
                  onClick={handleRetry}
                  disabled={isRetrying || !withWritePermission}
                >
                  <RefreshCwIcon className={`size-3 ${isRetrying ? "animate-spin" : ""}`} />
                  {isRetrying ? "Queuing..." : "Retry Post"}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Stats Section */}
        {isPosted && networkStats && (
          <div className="flex flex-col gap-2.5">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Post Performance</h4>
            <div className="grid grid-cols-2 gap-1.5">
              <StatItem icon={<Eye className="size-3.5" />} label="Views" value={networkStats?.viewCount} />
              <StatItem
                icon={<ThumbsUp className="size-3.5" />}
                label="Likes"
                value={networkStats?.likeCount || networkStats?.totalReactions}
              />
              <StatItem
                icon={<MessageSquare className="size-3.5" />}
                label="Comments"
                value={networkStats?.commentCount}
              />
              <StatItem icon={<Share2 className="size-3.5" />} label="Shares" value={networkStats?.shareCount} />
              <StatItem icon={<MousePointer2 className="size-3.5" />} label="Clicks" value={networkStats?.clickCount} />
              <StatItem
                icon={<Heart className="size-3.5" />}
                label="Reach"
                value={networkStats?.reachCount || networkStats?.impressionCount}
              />
            </div>
            {networkStats.timestamp && (
              <div className="text-xs text-slate-400 italic">Last checked {printDateTime(networkStats.timestamp)}</div>
            )}
          </div>
        )}

        {canDeletePost && (
          <div className="flex flex-col gap-2 mt-2.5">
            {!showDeleteConfirm ? (
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={!withWritePermission}
              >
                Delete Post on {socialNames[socialPost.socialNetworkId]}
              </Button>
            ) : (
              <div className="flex flex-col gap-2 p-3 rounded-xl border border-destructive/20 bg-destructive/5 animate-in fade-in slide-in-from-top-1">
                <div className="text-xs font-semibold text-destructive uppercase tracking-wider text-center">
                  Are you sure you want to delete this post?
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 h-8 text-xs hover:bg-white"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="flex-1 h-8 text-xs"
                    loading={isDeleting}
                    onClick={() => {
                      handleDelete(socialPost!.id);
                    }}
                  >
                    Yes, Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
